import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Button,
    Checkbox,
    Stack,
    Chip,
    Divider
} from "@mui/material";
import CustomModal from "../../../components/ui/CustomModal";
import { useParams } from "react-router-dom";
import { submitInjectCorporate } from "../../../APIConfig/NewCorporateApiConfig";
import { toast } from "react-toastify";
import ErrorHandler from "../../../ErrorHandler";

const Injects = ({ open, setModal, injectData, onSubmit }) => {
    const { id } = useParams()

    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [subjectiveAnswer, setSubjectiveAnswer] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const type = injectData?.inject_type;

    useEffect(() => {
        if (!open) {
            setSelectedOption(null);
            setSelectedOptions([]);
            setSubjectiveAnswer("");
            setSubmitting(false);
        }
    }, [open]);

    const handleSubmit = async () => {
        if (submitting) return;

        let payload = {};

        if (type === "mcq" || type === "true_false") {
            if (!selectedOption) {
                toast.warning("Please select an option before submitting.");
                return;
            }
            payload.selected_option_ids = [selectedOption];
        }

        if (type === "multiple_select") {
            if (!selectedOptions.length) {
                toast.warning("Please select at least one option before submitting.");
                return;
            }
            payload.selected_option_ids = selectedOptions;
        }

        if (type === "short_answer") {
            if (!subjectiveAnswer.trim()) {
                toast.warning("Please type your response before submitting.");
                return;
            }
            payload.response_text = subjectiveAnswer.trim();
        }

        setSubmitting(true);
        try {
            const inject_id = injectData?.id;

            const response = await submitInjectCorporate(
                id,
                inject_id,
                payload
            );

            const msg = response?.data?.detail;

            if (msg) {
                toast.success(msg);
            }


            if (response) {
                onSubmit?.(response?.data);
                setModal(false);
            }

        } catch (error) {
            ErrorHandler(error);
        } finally {
            setSubmitting(false);
        }
    };

    const renderContent = () => {
        switch (type) {

            case "mcq":
            case "true_false":
                return (
                    <RadioGroup
                        value={selectedOption}
                        onChange={(e) => setSelectedOption(Number(e.target.value))}
                    >
                        {injectData?.options?.map((opt) => (
                            <Box
                                key={opt.id}
                                sx={{
                                    border: "1px solid #2A2E39",
                                    borderRadius: "8px",
                                    px: 2,
                                    py: 1,
                                    mb: 1,
                                    "&:hover": { backgroundColor: "#1C1F28" }
                                }}
                            >
                                <FormControlLabel
                                    value={opt.id}
                                    control={<Radio />}
                                    label={opt.text}
                                    sx={{ width: "100%" }}
                                />
                            </Box>
                        ))}
                    </RadioGroup>
                );

            case "multiple_select":
                return (
                    <Stack>
                        {injectData?.options?.map((opt) => (
                            <Box
                                key={opt.id}
                                sx={{
                                    border: "1px solid #2A2E39",
                                    borderRadius: "8px",
                                    px: 2,
                                    py: 1,
                                    mb: 1,
                                    "&:hover": { backgroundColor: "#1C1F28" }
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedOptions.includes(opt.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedOptions(prev => [...prev, opt.id]);
                                                } else {
                                                    setSelectedOptions(prev =>
                                                        prev.filter(id => id !== opt.id)
                                                    );
                                                }
                                            }}
                                        />
                                    }
                                    label={opt.text}
                                    sx={{ width: "100%" }}
                                />
                            </Box>
                        ))}
                    </Stack>
                );

            case "short_answer":
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Type your response..."
                        value={subjectiveAnswer}
                        onChange={(e) => setSubjectiveAnswer(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#111218"
                            }
                        }}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <CustomModal
            open={open}
            sx={{ width: "80%", maxWidth: "650px" }}
            disableExternalClick
            onClose={() => setModal(false)}
            hideCloseIcon={false}
        >
            <Box>

                {/* Header */}
                <Box
                    sx={{
                        p: 3,
                        borderBottom: "1px solid #2A2E39",
                        backgroundColor: "#16181F",

                    }}
                >
                    <Typography variant="h3">
                        {injectData?.title}
                    </Typography>

                    <Stack direction="row" spacing={1} mt={1}>
                        <Chip
                            label={injectData?.inject_type_name}
                            size="small"
                            sx={{ backgroundColor: "#1C1F28" }}
                        />
                        <Chip
                            label={`${injectData?.points} pts`}
                            size="small"
                            color="primary"
                        />
                    </Stack>
                </Box>

                {/* Body */}
                <Box sx={{ p: 3 }}>
                    {renderContent()}
                </Box>

                <Divider />

                {/* Footer */}
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,

                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => setModal(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting
                            ? "Submitting..."
                            : (injectData?.response_text || injectData?.selected_options?.length ? ' Submit Again' : 'Submit Answer')}
                    </Button>
                </Box>

                {(injectData?.response_text || injectData?.selected_options?.length) && (
                    <Box sx={{ mt: 3 }}>
                        <>
                            <Typography
                                variant="body2"
                                sx={{ color: '#FFFFFF', fontWeight: 600, mb: 0.5 }}
                            >
                                Answer Submitted for Review :{" "}
                                {injectData?.response_text ||
                                    injectData?.selected_options?.[0]?.text}
                            </Typography>

                            <Typography variant="body2" sx={{ color: '#919EAB' }}>
                                Your answer has been submitted. Awaiting evaluation.
                            </Typography>
                        </>
                    </Box>
                )}
            </Box>
        </CustomModal>
    );
};

export default Injects;